import { type Static, Type } from "@sinclair/typebox";
import { AvatarSchema } from "../avatar/schema.ts";
import { REACTION_NAMES } from "../reactions.ts";

/** API の入力スキーマ。Elysia のルート定義がこれをそのまま使う。 */

/** 主キーは DB 採番の連番。 */
export const IdSchema = Type.Integer({ minimum: 1 });

export const AUTHOR_NAME_MAX = 40;
export const THREAD_TITLE_MAX = 120;
export const POST_BODY_MAX = 4000;

export const CreateThreadSchema = Type.Object(
  {
    categoryId: IdSchema,
    title: Type.String({ minLength: 1, maxLength: THREAD_TITLE_MAX }),
    /** 1 番目の投稿。スレッドは必ず本文と一緒に作る。 */
    body: Type.String({ minLength: 1, maxLength: POST_BODY_MAX }),
    allowAnonymousPost: Type.Optional(Type.Boolean({ default: true })),
  },
  { additionalProperties: false },
);

export const CreatePostSchema = Type.Object(
  {
    body: Type.String({ minLength: 1, maxLength: POST_BODY_MAX }),
    /**
     * 返信ボタンから送る返信先 seq。本文の先頭 `>>N` と同じ扱いになる。
     * 本文からも抽出するため、省略しても `>>N` を書けば返信になる。返信先は 1 件だけ。
     */
    replyToSeq: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  { additionalProperties: false },
);

/** 使える絵文字は REACTIONS が唯一の出所。ここはそれを写すだけ。 */
export const ReactionNameSchema = Type.Union(REACTION_NAMES.map((name) => Type.Literal(name)));

/**
 * 一覧に管理者だけのものを混ぜるか。
 * 管理者モードを切っているときは付けないので、admin でも普通の見え方になる。
 */
export const IncludeHiddenSchema = Type.Optional(Type.Boolean());

/** スレッドやカテゴリを伏せる / 戻す (管理者のみ)。 */
export const SetHiddenSchema = Type.Object(
  { hidden: Type.Boolean() },
  { additionalProperties: false },
);

export type SetHiddenInput = Static<typeof SetHiddenSchema>;

/** 投稿を伏せる / 戻す (管理者のみ)。 */
export const HidePostSchema = Type.Object(
  {
    hidden: Type.Boolean(),
    /** 伏せた理由。運用者の覚書で、閲覧者には出さない。 */
    reason: Type.Optional(Type.String({ maxLength: 200 })),
  },
  { additionalProperties: false },
);

/** ログイン中のユーザー自身の更新。送られたキーだけ変える。 */
export const UpdateMeSchema = Type.Object(
  {
    name: Type.Optional(Type.String({ minLength: 1, maxLength: AUTHOR_NAME_MAX })),
    handle: Type.Optional(Type.String({ pattern: "^[a-z0-9][a-z0-9_-]{2,31}$" })),
    avatar: Type.Optional(AvatarSchema),
  },
  { additionalProperties: false },
);

export const ListQuerySchema = Type.Object({
  cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 20 })),
});

export const ListThreadsQuerySchema = Type.Composite([
  ListQuerySchema,
  Type.Object({
    /** カテゴリ slug で絞る。子カテゴリは含まない。 */
    category: Type.Optional(Type.String({ minLength: 1, maxLength: 48 })),
    includeHidden: IncludeHiddenSchema,
  }),
]);

export const ListCategoriesQuerySchema = Type.Object({
  includeHidden: IncludeHiddenSchema,
});

export type ListCategoriesQuery = Static<typeof ListCategoriesQuerySchema>;

/**
 * スレッドの投稿。2 つの読み方がある。
 *
 * - `rootLimit` なし: seq の昇順に `limit` 件。CLI や bot が使う
 * - `rootLimit` あり: **新しい順に親ポスト N 件と、その下の返信すべて**。web の表示用。
 *   返信は親の下に出すので、件数で切ると木が途中で切れてしまうため親の数で数える。
 *   `cursor` は「そこより古い親から」の意味になる
 */
export const ListPostsQuerySchema = Type.Object({
  cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200, default: 50 })),
  rootLimit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
});

export type CreateThreadInput = Static<typeof CreateThreadSchema>;
export type CreatePostInput = Static<typeof CreatePostSchema>;
export type HidePostInput = Static<typeof HidePostSchema>;
export type UpdateMeInput = Static<typeof UpdateMeSchema>;
export type ListThreadsQuery = Static<typeof ListThreadsQuerySchema>;
export type ListPostsQuery = Static<typeof ListPostsQuerySchema>;

export const CreateCategorySchema = Type.Object(
  {
    parentId: Type.Optional(IdSchema),
    slug: Type.String({ pattern: "^[a-z0-9][a-z0-9-]{0,47}$" }),
    name: Type.String({ minLength: 1, maxLength: 60 }),
    description: Type.Optional(Type.String({ maxLength: 200 })),
    sortOrder: Type.Optional(Type.Integer({ minimum: 0, maximum: 9999 })),
  },
  { additionalProperties: false },
);

export type CreateCategoryInput = Static<typeof CreateCategorySchema>;

/** slug は変えない (URL が動くため)。null を渡すとトップに移す。 */
export const UpdateCategorySchema = Type.Object(
  {
    parentId: Type.Optional(Type.Union([IdSchema, Type.Null()])),
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 60 })),
    description: Type.Optional(Type.String({ maxLength: 200 })),
    sortOrder: Type.Optional(Type.Integer({ minimum: 0, maximum: 9999 })),
    /** 伏せる / 戻す。配下のカテゴリとスレッドもまとめて見えなくなる。 */
    hidden: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export type UpdateCategoryInput = Static<typeof UpdateCategorySchema>;

export const CreateBotSchema = Type.Object(
  {
    handle: Type.String({ pattern: "^[a-z0-9][a-z0-9_-]{2,31}$" }),
    name: Type.String({ minLength: 1, maxLength: 40 }),
  },
  { additionalProperties: false },
);

export type CreateBotInput = Static<typeof CreateBotSchema>;

export const IssueTokenSchema = Type.Object(
  {
    handle: Type.String({ pattern: "^[a-z0-9][a-z0-9_-]{2,31}$" }),
  },
  { additionalProperties: false },
);

export type IssueTokenInput = Static<typeof IssueTokenSchema>;

export const DecideDeviceSchema = Type.Object(
  {
    userCode: Type.String({ minLength: 1, maxLength: 32 }),
  },
  { additionalProperties: false },
);

export type DecideDeviceInput = Static<typeof DecideDeviceSchema>;

export const PollDeviceSchema = Type.Object(
  {
    deviceCode: Type.String({ minLength: 1, maxLength: 128 }),
  },
  { additionalProperties: false },
);

export type PollDeviceInput = Static<typeof PollDeviceSchema>;
